import { useCallback } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import type { AuthResponse, User } from '@twinden/shared';
import {
  accessTokenAtom,
  refreshTokenAtom,
  currentUserAtom,
  isAuthenticatedAtom,
  setAuthAtom,
  clearAuthAtom,
  offerPasskeyHereAtom,
} from '../store';
import {
  api,
  type HandleSuggestion,
  type JoinNewAccountBody,
} from '../lib/api';
import { forgetE2EE } from '../lib/e2ee/session';
import { forgetPictures } from '../lib/e2ee/pictures';
import { forgetShares } from '../lib/shares';

export function useAuth() {
  const [accessToken] = useAtom(accessTokenAtom);
  const [refreshToken] = useAtom(refreshTokenAtom);
  const currentUser = useAtomValue(currentUserAtom);
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const setCurrentUser = useSetAtom(currentUserAtom);
  const setAuth = useSetAtom(setAuthAtom);
  const clearSignedIn = useSetAtom(clearAuthAtom);
  // Signing out, by choice or not: the faces opened here go with their
  // keys, so the next person on this device never sees them (M72, D294).
  const clearAuth = useCallback(() => {
    forgetPictures();
    clearSignedIn();
  }, [clearSignedIn]);
  const setOfferPasskeyHere = useSetAtom(offerPasskeyHereAtom);

  // Sync tokens to API client
  api.setTokens(accessToken, refreshToken);

  const getMe = useCallback(async () => {
    const response = await api.getMe();
    if (response.success) {
      setCurrentUser(response.data as User);
    }
    return response;
  }, [setCurrentUser]);

  const logout = useCallback(async () => {
    try {
      // Best-effort: detach this device's push endpoint from the account so a
      // logged-out browser stops receiving the previous user's notifications.
      // The browser subscription is kept; enabling again re-registers it.
      if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          const subscription =
            await registration?.pushManager.getSubscription();
          if (subscription) await api.unsubscribePush(subscription.endpoint);
        } catch {
          // Never block logout on notification cleanup.
        }
      }
      // This device's encryption key goes too (M28, D55).
      await forgetE2EE().catch(() => undefined);
      // And anything shared to Twinden that wasn't sent (D178).
      await forgetShares();
      await api.logout();
    } finally {
      clearAuth();
    }
  }, [clearAuth]);

  // After deleting the account (M51): the server has dropped the session,
  // devices and push already, so only this device's copies go, then the
  // signed-out landing page shows.
  const signOutDeletedAccount = useCallback(async () => {
    try {
      if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          const subscription =
            await registration?.pushManager.getSubscription();
          await subscription?.unsubscribe();
        } catch {
          // Never block signing out on notification cleanup.
        }
      }
      await forgetE2EE({ server: false }).catch(() => undefined);
      // Anything shared to Twinden and not sent goes too (D178).
      await forgetShares().catch(() => undefined);
    } finally {
      clearAuth();
    }
  }, [clearAuth]);

  api.setCallbacks({
    onTokenRefresh: (_tokens) => {
      // This would need to update atoms, but we can't call hooks conditionally
      // In a real app, you'd use a context or event system
    },
    onAuthError: () => {
      clearAuth();
    },
    // Deleted here or on another device (M51): this device's copies go too.
    onAccountDeleted: () => {
      void signOutDeletedAccount();
    },
  });

  // Full enrollment ceremony (identity spec §5.1): fetch registration
  // options → authenticator creates a passkey → server verifies, creates
  // the user (or adds the device), consumes the token, returns a session.
  const enrollWithPasskey = useCallback(
    async (token: string, deviceName?: string, handle?: HandleSuggestion) => {
      // `handle`: a new person's pick on a link that holds none (D348).
      const startRes = await api.enrollPasskeyStart(token, handle);
      const attResp = await startRegistration({
        optionsJSON:
          startRes.data as unknown as PublicKeyCredentialCreationOptionsJSON,
      });
      const finishRes = await api.enrollPasskeyFinish(
        token,
        attResp,
        deviceName,
        handle
      );
      setAuth({ user: finishRes.data.user, tokens: finishRes.data.tokens });
      return finishRes.data;
    },
    [setAuth]
  );

  // Passkey login (identity spec §5.2): discoverable credentials — the
  // browser shows every passkey saved for this domain, user picks one.
  const loginWithPasskey = useCallback(async () => {
    const startRes = await api.passkeyLoginStart();
    const asseResp = await startAuthentication({
      optionsJSON:
        startRes.data as unknown as PublicKeyCredentialRequestOptionsJSON,
    });
    const finishRes = await api.passkeyLoginFinish(asseResp);
    setAuth({ user: finishRes.data.user, tokens: finishRes.data.tokens });
    // The passkey was on another device (M41, D93).
    setOfferPasskeyHere(asseResp.authenticatorAttachment === 'cross-platform');
    return finishRes.data;
  }, [setAuth, setOfferPasskeyHere]);

  // Sign in by scanning (M49, D160): the phone's passkey approved this
  // computer, which has no passkey of its own, so offer one like a
  // cross-platform sign-in does (D93).
  const completeQrSignIn = useCallback(
    (user: AuthResponse['user'], tokens: AuthResponse['tokens']) => {
      setAuth({ user, tokens });
      setOfferPasskeyHere(true);
    },
    [setAuth, setOfferPasskeyHere]
  );

  // A new account made with the family link (D153): the server makes the
  // account with no family and a pending request; the session starts now.
  const joinWithFamilyLink = useCallback(
    async (
      secret: string,
      input: Omit<JoinNewAccountBody, 'pendingUserId' | 'response'>
    ) => {
      const startRes = await api.joinLinkPasskeyStart(secret, {
        displayName: input.displayName,
        handle: input.handle,
        handleToken: input.handleToken,
      });
      const attResp = await startRegistration({
        optionsJSON: startRes.data
          .options as unknown as PublicKeyCredentialCreationOptionsJSON,
      });
      const finishRes = await api.joinLinkPasskeyFinish(secret, {
        ...input,
        pendingUserId: startRes.data.pendingUserId,
        response: attResp,
      });
      setAuth({
        user: { ...finishRes.data.user, awaitingFirstFamily: true },
        tokens: finishRes.data.tokens,
      });
      return finishRes.data;
    },
    [setAuth]
  );

  return {
    currentUser,
    isAuthenticated,
    getMe,
    logout,
    signOutDeletedAccount,
    setCurrentUser,
    enrollWithPasskey,
    loginWithPasskey,
    /** The join pages' "I already use Twinden" (§1a): the same sign-in. */
    signInWithPasskey: loginWithPasskey,
    completeQrSignIn,
    joinWithFamilyLink,
  };
}
